import React, {useContext, useEffect, useRef} from "react"
import {Bar, Line} from "react-chartjs-2"
import {getMonthHeaderWithYear, getMonthName} from "../insight/months"
import {MonthWithIndex} from "../../../services/api/insight-api"
import InsightDataHelper from "../insight/insight-data-helper"
import {Category} from "../../../domain/category"
import categoriesBarChartStyles from "../insight/categories-bar-chart/categories-bar-chart.module.scss"
import * as d3 from "d3"
import {formatAmount, formatImpact} from "../transactions/transaction-amount/transaction-amount"
import categoriesChartStyles from "../insight/categories-d3-chart/categories-d3-chart.module.scss"
import {CategoryHelper, CategoryHelperContext} from "../../../services/helpers/category-helper"
import styles from "./progress-chart.module.scss"
import {average} from "../../../util/compute-utils"
import {firstToUpper} from "../../../util/string-utils"

export type ProgressChartType = "line" | "bar"
export type IncomeOrExpenses = "income" | "expenses"

export interface ProgressD3ChartProps {
  spendingData: InsightDataHelper
  showCo2: boolean
  clickCategoryMonth: (categoryId: number | undefined, monthIndex: number) => void
  selectedProgressCategoryId: number | null
  incomeOrExpenses?: IncomeOrExpenses
  noSubCategories?: boolean
}

interface BarDataItem {
  month: MonthWithIndex
  value: number
  selectedCategoryValue?: number
}

export const ProgressD3Chart : React.FC<ProgressD3ChartProps> = ({ spendingData, showCo2, clickCategoryMonth, selectedProgressCategoryId, incomeOrExpenses = "expenses", noSubCategories = false }) => {
  const categoryHelper = useContext<CategoryHelper | null>(CategoryHelperContext)
  const category = selectedProgressCategoryId === null ? null : categoryHelper?.categoriesById[selectedProgressCategoryId]
  const categoryColor = category?.color ?? "#ddd"
  const darkGrey = "#767676"
  const incomeGreen = "#31952f"
  const co2Blue = "#004c6d"

  const ref = useRef(null)

  const n = spendingData.monthlySpendingData.months.length
  const margin = ({top: 16, right: 6, bottom: 6, left: 0})
  const barSize = 54
  const betweenBars = 18
  const width = margin.left + (barSize) * n + betweenBars * (n-1) + margin.right

  function getBarDataset(spendingData: InsightDataHelper, selectedProgressCategoryId: number | null, showCo2: boolean): BarDataItem[] {
    const totalByMonth = showCo2 ? spendingData.totalCo2ByMonth : (incomeOrExpenses === "expenses" ? spendingData.totalSpendingByMonth : spendingData.totalIncomeByMonth)
    const byMonthByCategory = getByMonthByCategory(selectedProgressCategoryId, showCo2, spendingData)

    return Object.keys(totalByMonth).map(monthIndex => {
      const monthIndexAsNumber = parseInt(monthIndex)

      return {
        month: spendingData.monthlySpendingData.months[monthIndexAsNumber],
        value: totalByMonth[monthIndexAsNumber],
        selectedCategoryValue: selectedProgressCategoryId === null ? undefined : byMonthByCategory[selectedProgressCategoryId][monthIndexAsNumber]
      }
    })
  }

  useEffect(() => {
    const chartDiv = d3.select(ref.current)

    chartDiv.select("svg").remove()

    const spaceForValueAbove = 25
    const spaceForValueBelow = 25
    const height = 350

    const svg = chartDiv
      .append("svg")
      .attr("height", height)
      .attr("width", width)

    const heightForBars = height - spaceForValueBelow - spaceForValueAbove

    const barDataset = getBarDataset(spendingData, selectedProgressCategoryId, showCo2)
    const averageValue = average(barDataset.map(x => x.value))

    const max = barDataset.map(x => x.value)
      .reduce((previousMax, currentNumber) => Math.max(previousMax, Math.abs(currentNumber)))
    const scaleValue = (value: number) => {
      return Math.abs(value / max * heightForBars)
    }

    function getScaledCategoryHeight(d: BarDataItem) {
      return d.selectedCategoryValue === undefined || noSubCategories ? 0 : scaleValue(d.selectedCategoryValue)
    }

    const getXForIndex = (i: number) => 5 + i*(barSize + betweenBars)
    const formatValue = (value: number) => {
      return showCo2
        ? formatImpact(Math.abs(value), 0)
        : formatAmount(Math.abs(value), 0)
    }

    const mapValueToY = (value: number): number => {
      return height - spaceForValueBelow - scaleValue(value)
    }

    const showSubCategory = !noSubCategories && selectedProgressCategoryId !== null

    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", mapValueToY(averageValue))
      .attr("y2", mapValueToY(averageValue))
      .attr("stroke", darkGrey)

    if (showSubCategory) {
      const averageCategoryValue = average(barDataset.map(x => x.selectedCategoryValue ?? 0))

      svg.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", mapValueToY(averageCategoryValue))
        .attr("y2", mapValueToY(averageCategoryValue))
        .attr("stroke", categoryColor)
    }

    svg.selectAll("text")
      .data(barDataset, (d) => (d as BarDataItem).month.month)
      .enter()
      .append("text")
      .attr("text-anchor", "middle")
      .attr("x", (d, i) => getXForIndex(i) + barSize / 2)
      .attr("y", height - 21)
      .text(d => `${firstToUpper(getMonthName(new Date(d.month.month)).substr(0, 3))} ${new Date(d.month.month).getFullYear().toString().substr(2, 2)}`)
      .attr("font-size", 14)
      .attr("fill", darkGrey)
      .attr("dy", "1em")

    svg.selectAll("rect")
      .data(barDataset, (d) => (d as BarDataItem).month.month)
      .enter()
      .append("rect")
      .attr("x", (d, i) => getXForIndex(i))
      .attr("y", d => mapValueToY(d.value))
      .attr("width", barSize)
      .attr("height", d => scaleValue(d.value) - getScaledCategoryHeight(d))
      .attr("fill", incomeOrExpenses === "expenses" ? (showCo2 ? co2Blue : darkGrey) : incomeGreen)
      // .attr("fill-opacity", 0.8)
      .attr("class", categoriesChartStyles.ClickableBar)
      .on("click", (e, d) => {
        clickCategoryMonth(noSubCategories ? (selectedProgressCategoryId ?? undefined) : undefined, d.month.index)
      })

    svg.selectAll("q")
      .data(barDataset, (d) => (d as BarDataItem).month.month)
      .enter()
      .append("text")
      .attr("text-anchor", "middle")
      .attr("x", (d, i) => getXForIndex(i) + barSize / 2)
      .attr("y", d => mapValueToY(d.value) - 20)
      .text(d => formatValue(d.value))
      .attr("font-size", 14)
      .attr("dy", "1em")

    if (showSubCategory) {
      svg.selectAll("rect2")
        .data(barDataset, (d) => (d as BarDataItem).month.month)
        .enter()
        .append("rect")
        .attr("x", (d, i) => getXForIndex(i))
        .attr("y", d => height - spaceForValueBelow - getScaledCategoryHeight(d))
        .attr("width", barSize)
        .attr("height", d => getScaledCategoryHeight(d))
        .attr("fill", categoryColor)
        .attr("fill-opacity", 1)
        .attr("class", categoriesChartStyles.ClickableBar)
        .on("click", (e, d) => {
          clickCategoryMonth(selectedProgressCategoryId!, d.month.index)
        })

      svg.selectAll("q2")
        .data(barDataset, (d) => (d as BarDataItem).month.month)
        .enter()
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", (d, i) => getXForIndex(i) + barSize / 2)
        .attr("y", d => height - spaceForValueBelow - getScaledCategoryHeight(d) - 20)
        // .attr("width", 40)
        .text(d => formatValue(d.selectedCategoryValue ?? 0))
        // .attr("fill-opacity", 0.7)
        .attr("font-weight", "bold")
        .attr("font-size", 14)
        .attr("fill", "#fff")
        .attr("dy", "1em")
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendingData, selectedProgressCategoryId, showCo2])

  return <div ref={ref} className={styles.Container} style={{maxWidth: width+ 32, paddingLeft: 16, paddingRight: 16}} data-hj-suppress={true}>
  </div>
}

export interface ProgressChartProps {
  spendingData: InsightDataHelper
  showCo2: boolean
  clickCategoryMonth: (categoryId: number, monthIndex: number) => void
  chartType: ProgressChartType
  selectedProgressCategoryId: number | null
  incomeOrExpenses?: IncomeOrExpenses
}

function getByMonthByCategory(selectedProgressCategoryId: number | null, showCo2: boolean, spendingData: InsightDataHelper) {
  return selectedProgressCategoryId
    ? (showCo2
      ? spendingData.monthlySpendingData.co2ByMonthByCategory
      : spendingData.monthlySpendingData.spendingByMonthByCategory)
    : (showCo2
      ? spendingData.co2ByMonthByTopCategory
      : spendingData.spendingByMonthByTopCategory)
}

export const ProgressChart : React.FC<ProgressChartProps> = ({ spendingData, showCo2, clickCategoryMonth, chartType, selectedProgressCategoryId, incomeOrExpenses = "expenses" }) => {
  const categories = selectedProgressCategoryId
    ? spendingData.applicableCategories.filter(c => c.parentId === selectedProgressCategoryId && c.isIncome === (incomeOrExpenses === "income"))
    : spendingData.applicableCategories.filter(c => c.parentId == null && c.isIncome === (incomeOrExpenses === "income"))

  const byMonthByCategory = getByMonthByCategory(selectedProgressCategoryId, showCo2, spendingData)

  const lineData = getLineData(categories, byMonthByCategory, spendingData.monthlySpendingData.months)
  const chartOnElementsClick = async (e: any) => {
    const clickedElement = e[0]
    if (clickedElement == null) return

    const category = categories[e[0]._datasetIndex]
    const categoryId = category.id

    clickCategoryMonth(categoryId, e[0]._index)
  }

  let barOptions = {
    scales: {
      xAxes: [{
        stacked: true
      }],
      yAxes: [{
        stacked: true
      }]
    },
    legend: {
      onClick: function() { }
    },
    maintainAspectRatio: false,
  }

  let lineOptions = {
    legend: {
      onClick: function() { }
    },
    maintainAspectRatio: false,
  }

  return <div className={categoriesBarChartStyles.Chart}>
    {(chartType === "bar")
      ? <Bar data={lineData} options={barOptions} getElementAtEvent={chartOnElementsClick} height={60} width={80}/>
      : <Line data={lineData} options={lineOptions} getElementAtEvent={chartOnElementsClick} height={20} width={80}/>
    }
  </div>
}

function getLineData(categories: Category[], byMonthByCategory: Record<number, Record<number, number>>, months: MonthWithIndex[]) {
  const monthHeadersWithYear = months.map(m => getMonthHeaderWithYear(m))

  return {
    labels: monthHeadersWithYear,
    datasets: categories.map((c) => getLineDataSet(c, c.color, byMonthByCategory))
  }
}

function getLineDataSet(category: Category, color: string, byMonthByCategory: Record<number, Record<number, number>>) {
  return {
    label: category.name,
    fill: false,
    backgroundColor: color,
    borderColor: color,
    lineTension: 0,
    data: Object.values(byMonthByCategory[category.id]).map(v => Math.abs(v))
  }
}
